import type { Me } from "@/lib/api";
import { ChevronRight, Menu } from "lucide-react";
import { Link, useLocation } from "react-router";
import { AccountMenu } from "./AccountMenu";

type Crumb = { label: string; to: string };
const labels: Record<string, string> = {
  projects: "Projects",
  secrets: "Secrets",
  settings: "Settings",
  members: "Members",
  rotators: "Rotators",
  new: "New secret",
  import: "Import",
  "service-tokens": "Service tokens",
};
function decode(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// Environments have no standalone route. Secret paths may contain slashes.
export function crumbsFromPath(pathname: string): Crumb[] {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return [{ label: "Overview", to: "/" }];
  const crumbs: Crumb[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part || (parts[0] === "projects" && i === 2 && part === "envs")) continue;
    const to = `/${parts.slice(0, i + 1).join("/")}`;
    if (parts[0] === "projects" && i > 5) break;
    if (parts[0] === "projects" && i === 5) {
      const remainder = parts.slice(5).join("/");
      crumbs.push({ label: labels[remainder] ?? decode(remainder), to: pathname });
    } else {
      const isSlug = parts[0] === "projects" && (i === 1 || i === 3);
      crumbs.push({
        label: isSlug ? decode(part) : (labels[part] ?? decode(part)),
        to: i === 3 && parts[2] === "envs" ? `${to}/secrets` : to,
      });
    }
  }
  return crumbs;
}

export function TopBar({ me, onOpenSidebar }: { me: Me; onOpenSidebar?: () => void }) {
  const crumbs = crumbsFromPath(useLocation().pathname);
  return (
    <header className="console-topbar">
      <div className="flex min-w-0 items-center gap-3">
        {onOpenSidebar && (
          <button
            type="button"
            onClick={onOpenSidebar}
            aria-label="Open navigation"
            className="mobile-nav-trigger"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
        )}
        <nav aria-label="Breadcrumb" className="breadcrumb">
          <ol>
            {crumbs.map((crumb, i) => (
              <li key={`${crumb.to}-${i}`}>
                {i > 0 && <ChevronRight size={13} aria-hidden="true" />}
                {i === crumbs.length - 1 ? (
                  <span aria-current="page">{crumb.label}</span>
                ) : (
                  <Link to={crumb.to}>{crumb.label}</Link>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>
      <AccountMenu me={me} />
    </header>
  );
}
